import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, AlertTriangle, BarChart3, FileText, FlaskConical, LogOut, Menu, MessageSquareWarning, Phone, RefreshCw, Settings, Users, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { relTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/common";

const NAV = [
  { to: "/", label: "Overview", icon: Activity, end: true },
  { to: "/calls", label: "Calls", icon: Phone },
  { to: "/agents", label: "Agents", icon: BarChart3, admin: true },
  { to: "/texts", label: "Text SLA", icon: MessageSquareWarning },
  { to: "/anomalies", label: "Anomalies", icon: AlertTriangle, admin: true },
  { to: "/reports", label: "Reports", icon: FileText, admin: true },
  { to: "/users", label: "Users", icon: Users, admin: true },
  { to: "/settings", label: "Settings", icon: Settings, admin: true },
];

function SyncButton() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sync-status"], queryFn: () => api.get("/sync/status").then((r) => r.data), refetchInterval: 15000 });
  const m = useMutation({
    mutationFn: () => api.post("/sync/run", { full: false }).then((r) => r.data),
    onSuccess: (d) => {
      toast(d.started ? "OpenPhone sync started" : d.message);
      setTimeout(() => qc.invalidateQueries(), 8000);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="flex items-center gap-2">
      <span className="hidden md:inline text-[11px] font-mono text-slate-500" data-testid="last-sync-label">
        {data?.running ? "syncing…" : data?.last_sync ? `synced ${relTime(data.last_sync)}` : "not synced yet"}
      </span>
      <Button data-testid="sync-now-button" size="sm" variant="outline" className="h-8 border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30] text-xs" onClick={() => m.mutate()} disabled={m.isPending || data?.running}>
        <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", (m.isPending || data?.running) && "animate-spin")} /> Sync
      </Button>
    </div>
  );
}

function SandboxBanner() {
  const { data } = useQuery({ queryKey: ["status"], queryFn: () => api.get("/settings/status").then((r) => r.data), refetchInterval: 60000 });
  if (!data?.sandbox_mode) return null;
  return (
    <div className="bg-amber-500/15 border-b border-amber-500/40 text-amber-200 text-xs px-4 sm:px-6 py-1.5 flex flex-wrap items-center gap-2" data-testid="sandbox-banner">
      <FlaskConical className="h-3.5 w-3.5" />
      <span className="font-mono font-bold tracking-wider">SANDBOX MODE</span>
      <span className="text-amber-200/80">live OpenPhone data · all emails redirected to {data.sandbox_recipients?.join(", ")} · Slack prefixed</span>
      <Link to="/settings" className="ml-auto underline hover:text-white">Switch to live delivery</Link>
    </div>
  );
}

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const items = NAV.filter((n) => !n.admin || isAdmin);

  const sidebar = (
    <aside className="w-64 shrink-0 h-full flex flex-col border-r border-[#1f293d] bg-[#0f172a]/95 backdrop-blur-md">
      <div className="px-5 py-5 border-b border-[#1f293d]">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-md bg-blue-600 inline-flex items-center justify-center shadow-[0_0_20px_-3px_rgba(59,130,246,0.6)]">
            <Phone className="h-4 w-4 text-white" />
          </span>
          <div>
            <div className="font-display font-extrabold text-white leading-none tracking-tight">TQI Pulse</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mt-1">Conversation Intel</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors border border-transparent",
                isActive ? "bg-blue-600/15 text-white border-blue-500/30" : "text-slate-400 hover:text-white hover:bg-[#161f30]"
              )
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-[#1f293d]">
        <div className="flex items-center gap-2.5">
          <Avatar name={user?.name || user?.email} />
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-100 truncate" data-testid="current-user-name">{user?.name || user?.email}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500" data-testid="current-user-role">{user?.role}</div>
          </div>
          <button data-testid="logout-button" onClick={() => { logout(); nav("/login"); }} className="text-slate-500 hover:text-white transition-colors" title="Sign out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex text-slate-100">
      <div className="hidden md:block sticky top-0 h-screen">{sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          <div className="h-full">{sidebar}</div>
          <button className="flex-1 bg-black/60" onClick={() => setOpen(false)} aria-label="Close menu" />
          <button className="absolute top-4 right-4 text-white" onClick={() => setOpen(false)} aria-label="Close">
            <X className="h-6 w-6" />
          </button>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 h-14 glass border-b border-[#1f293d] px-4 sm:px-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 hidden sm:block">Total Quality Insurance · Command Center</div>
          </div>
          <div className="flex items-center gap-2">{isAdmin && <SyncButton />}</div>
        </header>
        {isAdmin && <SandboxBanner />}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-[1600px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
